import { useMemo } from 'react';
import { Layers, Package, Settings } from 'react-feather';
import { Role } from '~/common/providers/auth/role';
import { financialSheetsAllowedRoles } from '~/features/financial-sheets/shared/financial-sheets-allowed-roles';
import { EnabledFeatureMap } from '~/common/hooks/use-enabled-feature-map';
import {
  analyticsMenuAllowedRoles,
  analyticsMenuViewAccountAllowedRoles,
} from '~/features/analytics-menu/analytics-menu-allowed-role';
import { planningAllowedRoles } from '~/features/plannings/index/plannings-allowed-roles';
import { allocationsAllowedRoles } from '~/features/allocation/shared/allocation-allowed-roles';
import { uploadAllowedRoles } from '~/features/upload/common/upload-allowed-role';
import * as roles from '~/features/settings/settings-allowed-roles';
import { settingsViewPermissionsAllowedRoles } from '~/features/settings/settings-allowed-roles';
import { planUpdateRequestMappingAllowedRoles } from '~/features/plan-update-request-mapping/plan-update-request-mapping-allowed-roles';
import { planUpdateRequestFormatAllowedRoles } from '~/features/plan-update-request-format/plan-update-request-format-allowed-roles';
import { planUpdateRequestFileAllowedRoles } from '~/features/plan-update-request-file/plan-update-request-file-allowed-roles';
import { useRoleGuard } from '~/common/providers/auth/use-role-guard';
import { useFeatureGuard } from '~/common/guards/use-feature-guard';
import type { NavigationMenuItem } from '~/common/components/layout/navigation/NavigationBody';
import { CURRENCIES_ALLOWED_ROLES } from '~/features/currencies/currencies-allowed-roles';
import { useMyRole } from '~/common/providers/auth/use-login-user';
import { financialSimulationAllowedRoles } from '~/features/financial-simulations/financial-simulation-allowed-roles';
import { allocationsAllowedRolesV2 } from '~/features/allocation/v2/shared/allocation-allowed-roles';
import { dataDownloadAllowedRoles } from '~/features/data-download/data-download-allowed-role';
import { dashboardAllowedRoles } from '~/features/workbook/DashboardRoleGuard';
import { useWorkforcePlanningNavigationItems } from '~/features/workforce-planning/common/use-workforce-planning-navigation-items';

type UseNavigationItemsProps = {
  currentUrl: string;
  enabledFeatureMap: EnabledFeatureMap;
};

const useNavigationItems = ({ currentUrl, enabledFeatureMap }: UseNavigationItemsProps) => {
  const isCurrentPage = (targetUrl: string) => {
    // クエリパラメータとハッシュを除外したパスを取得
    const pathWithoutQuery = currentUrl.split('?')[0].split('#')[0];
    if (targetUrl === pathWithoutQuery) return true;
    return pathWithoutQuery.startsWith(`${targetUrl}/`); // NOTE: 子ページに遷移してもActiveになるように末尾に`/`を入れている
  };
  const myRole = useMyRole();

  const isShowDashboardMenu = useMemo(
    () =>
      enabledFeatureMap.isTableauDashboardEnabled &&
      myRole &&
      (analyticsMenuAllowedRoles.includes(myRole) ||
        analyticsMenuViewAccountAllowedRoles.includes(myRole)),
    [myRole, enabledFeatureMap.isTableauDashboardEnabled],
  );

  const isFinancialSheetHistoryAndDepartmentEnabled = useMemo(
    () => enabledFeatureMap?.isFinancialSheetHistoryAndDepartmentEnabled,
    [enabledFeatureMap?.isFinancialSheetHistoryAndDepartmentEnabled],
  );

  const permittedDataDownload = useRoleGuard(dataDownloadAllowedRoles);

  const permittedFinancialSheets = useRoleGuard(financialSheetsAllowedRoles);
  const permittedFinancialSimulation = useRoleGuard(financialSimulationAllowedRoles);

  const permittedPlanning = useRoleGuard(planningAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING', 'MANAGER'
  const permittedSettingsPlans = useRoleGuard(roles.settingsPlansAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'
  const permittedPlanUpdateRequestFile = useRoleGuard(planUpdateRequestFileAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'
  const permittedPlanUpdateRequestFormat = useRoleGuard(planUpdateRequestFormatAllowedRoles);
  const permittedPlanUpdateRequestMapping = useRoleGuard(planUpdateRequestMappingAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'
  const permittedUpload = useRoleGuard(uploadAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'
  const permittedAllocations = useRoleGuard(allocationsAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'
  const permittedAllocationsV2 = useRoleGuard(allocationsAllowedRolesV2); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'

  const permittedCurrenciesAllowedRoles = useRoleGuard(CURRENCIES_ALLOWED_ROLES); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'

  const permittedMasterManagement = useRoleGuard(MASTER_MANAGEMENT_ALLOWED_ROLES); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING', 'IT_STAFF'
  const permittedSettingsOrganization = useRoleGuard(roles.settingsOrganizationAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING', 'IT_STAFF'
  const permittedSettingsAccountTree = useRoleGuard(roles.settingsAccountTreeAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'
  const permittedSettingsDimensions = useRoleGuard(roles.settingsDimensionsAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'
  const permittedSettingsUnitPrice = useRoleGuard(roles.settingsUnitPricesAllowedRoles); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'CORP_PLANNING'

  const permittedSettings = useRoleGuard(SETTINGS_ALLOWED_ROLES); // 'LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'IT_STAFF'
  const permittedSettingsViewPermissions = useRoleGuard(settingsViewPermissionsAllowedRoles);
  const permittedDashboard = useRoleGuard(dashboardAllowedRoles);

  const isUnitPriceDataImportActualEnabled = useFeatureGuard([
    'isUnitPriceDataImportActualEnabled',
  ]);
  const isViewPermissionEnabled = useFeatureGuard(['isViewPermissionEnabled']);
  const isForeignCurrencyEnabled = useFeatureGuard(['isForeignCurrencyEnabled']);
  const isNewDashboardEnabled = useFeatureGuard(['isNewDashboardEnabled']);

  const { workforcePlanningSection } = useWorkforcePlanningNavigationItems(isCurrentPage);

  const items: NavigationMenuItem[] = [
    permittedFinancialSheets && {
      key: 'financial-sheets',
      text: '経営分析',
      icon: <img alt="spreadsheeticon" src="/assets/icons/spreadsheet.svg" />,
      children: [
        isNewDashboardEnabled &&
          permittedDashboard && {
            key: 'dashboard',
            text: 'ダッシュボード',
            targetUrl: '/workbooks',
            isActive: isCurrentPage('/workbooks'),
          },
        {
          key: 'financial-sheets-reports',
          text: 'レポート',
          targetUrl: '/financial-sheets/reports',
          isActive: isCurrentPage('/financial-sheets/reports'),
        },
        //外貨機能が有効になっている場合、ナビゲーションアイテムは非表示となる
        isFinancialSheetHistoryAndDepartmentEnabled &&
          !isForeignCurrencyEnabled && {
            key: 'financial-sheets-history',
            text: '推移表',
            targetUrl: '/financial-sheets/history',
            isActive: isCurrentPage('/financial-sheets/history'),
          },
        !isForeignCurrencyEnabled && {
          key: 'financial-sheets-comparison',
          text: '対比表',
          targetUrl: '/financial-sheets/comparison',
          isActive: isCurrentPage('/financial-sheets/comparison'),
        },
        isFinancialSheetHistoryAndDepartmentEnabled &&
          !isForeignCurrencyEnabled && {
            key: 'financial-sheets-department',
            text: '部署別表',
            targetUrl: '/financial-sheets/department',
            isActive: isCurrentPage('/financial-sheets/department'),
          },
        isFinancialSheetHistoryAndDepartmentEnabled &&
          !isForeignCurrencyEnabled && {
            key: 'financial-sheets-department-comparison',
            text: '部署別対比表',
            targetUrl: '/financial-sheets/department-comparison',
            isActive: isCurrentPage('/financial-sheets/department-comparison'),
          },
        enabledFeatureMap.isFinancialSimulatorEnabled &&
          permittedFinancialSimulation && {
            key: 'financial-simulations',
            text: 'シミュレーション',
            targetUrl: '/financial-simulations',
            isActive: isCurrentPage('/financial-simulations'),
          },
        isShowDashboardMenu && {
          key: 'dashboard-tableau',
          text: isNewDashboardEnabled ? 'ダッシュボード (Tableau)' : 'ダッシュボード',
          targetUrl: '/dashboard',
          isActive: isCurrentPage('/dashboard'),
        },
        permittedDataDownload && {
          key: 'data-download',
          text: 'データダウンロード',
          targetUrl: '/data-download',
          isActive: isCurrentPage('/data-download'),
        },
      ].filter(truthy),
    },
    permittedPlanning && {
      key: 'planning',
      text: '経営データ更新',
      icon: <Layers />,
      children: [
        {
          key: '予算・見込',
          id: 'budgets-forecasts',
          text: '予算・見込',
          type: 'group' as const,
          children: [
            permittedSettingsPlans && {
              key: 'plans',
              text: '計画',
              targetUrl: '/plans',
              isActive: isCurrentPage('/plans'),
            },
            {
              key: 'plannings',
              text: '編成プロジェクト',
              targetUrl: '/plannings',
              isActive: isCurrentPage('/plannings'),
            },
            permittedPlanUpdateRequestFile && {
              key: 'plan-update-request-files',
              text: '申請済ファイル',
              targetUrl: '/plan-update-request-files',
              isActive: isCurrentPage('/plan-update-request-files'),
            },
            permittedPlanUpdateRequestFormat && {
              key: 'plan-update-request-formats',
              text: '申請フォーマット',
              targetUrl: '/plan-update-request-formats',
              isActive: isCurrentPage('/plan-update-request-formats'),
            },
            permittedPlanUpdateRequestMapping && {
              key: 'plan-update-request-mappings',
              text: '取込・出力設定',
              targetUrl: '/plan-update-request-mappings',
              isActive: isCurrentPage('/plan-update-request-mappings'),
            },
          ].filter(truthy),
        },
        permittedUpload && {
          key: 'actuals',
          id: 'actuals',
          text: '実績',
          type: 'group' as const,
          children: [
            {
              key: 'upload-financial-actuals',
              text: '財務実績',
              targetUrl: '/upload/financial-actuals',
              isActive: isCurrentPage('/upload/financial-actuals'),
            },
            {
              key: 'upload-non-financial-actuals',
              text: '非財務実績',
              targetUrl: '/upload/non-financial-actuals',
              isActive: isCurrentPage('/upload/non-financial-actuals'),
            },
            {
              key: 'actuals-import-mappings',
              text: '取込設定',
              targetUrl: '/actuals-import-mappings',
              isActive: isCurrentPage('/actuals-import-mappings'),
            },
            {
              key: 'process-settings',
              text: '加工設定',
              targetUrl: '/process-settings',
              isActive: isCurrentPage('/process-settings'),
            },
          ].filter(truthy),
        },
        permittedAllocations &&
          !enabledFeatureMap.isAllocationOldDeactivationEnabled && {
            key: 'allocations',
            id: 'allocations',
            text: '振替・配賦（従来版）',
            type: 'group' as const,
            children: [
              {
                key: 'allocations-standards',
                text: '配賦基準',
                targetUrl: '/allocations/standards',
                isActive: isCurrentPage('/allocations/standards'),
              },
              {
                key: 'allocations-rules',
                text: '振替・配賦ルール',
                targetUrl: '/allocations/rules',
                isActive: isCurrentPage('/allocations/rules'),
              },
              {
                key: 'allocations-strategies',
                text: '実行計画',
                targetUrl: '/allocations/strategies',
                isActive: isCurrentPage('/allocations/strategies'),
              },
            ].filter(truthy),
          },
        permittedAllocationsV2 && {
          key: 'allocations-v2',
          id: 'allocations-v2',
          text: '配賦・振替',
          type: 'group' as const,
          children: [
            {
              key: 'allocations-v2-rules',
              text: '配賦・振替ルール',
              targetUrl: '/allocations/v2/rules',
              isActive: isCurrentPage('/allocations/v2/rules'),
            },
            {
              key: 'allocations-v2-patterns',
              text: '配賦・振替パターン',
              targetUrl: '/allocations/v2/patterns',
              isActive: isCurrentPage('/allocations/v2/patterns'),
            },
            {
              key: 'allocations-v2-strategies',
              text: '配賦・振替実行',
              targetUrl: '/allocations/v2/executions',
              isActive: isCurrentPage('/allocations/v2/executions'),
            },
          ].filter(truthy),
        },
      ].filter(truthy),
    },
    workforcePlanningSection,
    permittedMasterManagement && {
      key: 'master',
      text: 'マスタ管理',
      icon: <img alt="spreadsheeticon" src="/assets/icons/allocation.svg" />,
      children: [
        permittedSettingsOrganization && {
          // LOG-8924 KARTE でチュートリアルを作るために実験的に追加。後ほど削除する
          id: 'nav-organization',
          key: 'master-organization',
          text: '組織',
          targetUrl: '/master/organization',
          isActive: isCurrentPage('/master/organization'),
        },
        permittedSettingsAccountTree && {
          key: 'accounts',
          id: 'accounts',
          text: '科目',
          type: 'group' as const,
          children: [
            {
              // LOG-8924 KARTE でチュートリアルを作るために実験的に追加。後ほど削除する
              id: 'nav-account-tree',
              key: 'master-account-pl',
              text: '財務科目',
              targetUrl: '/master/account/pl',
              isActive:
                isCurrentPage('/master/account/pl') ||
                isCurrentPage('/master/account/bs-account') ||
                isCurrentPage('/master/account/cf') ||
                isCurrentPage('/master/account/not-included'),
            },
            {
              key: 'master-account-non-financial',
              text: '非財務科目',
              targetUrl: '/master/account/non-financial',
              isActive: isCurrentPage('/master/account/non-financial'),
            },
            {
              key: 'master-account-calculated-account',
              text: '計算科目',
              targetUrl: '/master/account/calculated-account',
              isActive: isCurrentPage('/master/account/calculated-account'),
            },
          ].filter(truthy),
        },
        permittedSettingsDimensions && {
          key: 'master-dimensions',
          text: '分析軸',
          targetUrl: '/master/dimensions',
          isActive: isCurrentPage('/master/dimensions'),
        },
        isForeignCurrencyEnabled &&
          permittedCurrenciesAllowedRoles && {
            id: 'currencies',
            key: 'currencies',
            text: '通貨換算',
            type: 'group' as const,
            children: [
              {
                id: 'nav-local-currency',
                key: 'currencies-local-currency',
                text: '現地通貨',
                targetUrl: '/currencies/local-currency',
                isActive: isCurrentPage('/currencies/local-currency'),
              },
              {
                id: 'nav-rates',
                key: 'currencies-rates',
                text: '為替レート',
                targetUrl: '/currencies/rates',
                isActive: isCurrentPage('/currencies/rates'),
              },
            ].filter(truthy),
          },
      ].filter(truthy),
    },
    isUnitPriceDataImportActualEnabled &&
      permittedSettingsUnitPrice && {
        key: 'unit-price',
        text: '商品単価',
        icon: <Package />,
        children: [
          {
            key: 'unit-prices-and-unit-price-master-items',
            text: '商品単価',
            targetUrl: '/unit-prices',
            isActive: isCurrentPage('/unit-prices'),
          },
          {
            key: 'account-conversion-steps',
            text: '科目変換ルール',
            targetUrl: '/unit-prices/account-conversion-steps',
            isActive: isCurrentPage('/unit-prices/account-conversion-steps'),
          },
        ].filter(truthy),
      },
    permittedSettings && {
      key: 'settings',
      text: '設定',
      icon: <Settings />,
      children: [
        {
          key: 'settings-tenant',
          text: '会社設定',
          targetUrl: '/settings/tenant',
          isActive: isCurrentPage('/settings/tenant'),
        },
        {
          key: 'settings-users',
          text: 'ユーザー管理',
          targetUrl: '/settings/users',
          isActive: isCurrentPage('/settings/users'),
        },
        isViewPermissionEnabled &&
          permittedSettingsViewPermissions && {
            key: 'settings-view-permissions',
            text: '閲覧権限管理',
            targetUrl: '/settings/view-permissions',
            isActive: isCurrentPage('/settings/view-permissions'),
          },
      ].filter(truthy),
    },
  ].filter(truthy);

  return items;
};

// マスタ管理にアクセスできるロール群
const MASTER_MANAGEMENT_ALLOWED_ROLES: Role[] = [
  'LOGLASS_SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'CORP_PLANNING',
  'IT_STAFF',
];

// 設定にアクセスできるロール群
const SETTINGS_ALLOWED_ROLES: Role[] = ['LOGLASS_SYSTEM_ADMIN', 'TENANT_ADMIN', 'IT_STAFF'];

type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T;

function truthy<T>(value: T): value is Truthy<T> {
  return !!value;
}

export { useNavigationItems };
